<template>
  <v-dialog
    v-model="dialog"
    width="1024px"
    @click:outside="onClose"
    @keydown.esc="onClose"
  >
    <div class="dialog-container">
      <map-component
        v-if="dialog"
        @created="onMapCreated"
        @style-change="onMapCreated"
      />
    </div>
  </v-dialog>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import turfBbox from '@turf/bbox';
import MapBox from 'mapbox-gl';
import MapComponent from '@/components/map';
import LayerGeometryFeatureRequest from '@/utils/requests/layers/layer-geometry-feature';
import getGeometryType from '@/utils/map-box/get-geometry-type';
import DialogBusMixin from '../mixins/dialog-bus-mixin';

const polygonLayer = (sourceID, { color = '#26FF00', opacity = 0.5 }) => ({
  id: 'polygon-layer',
  source: sourceID,
  type: 'fill',
  layout: {},
  paint: {
    'fill-color': color,
    'fill-opacity': opacity,
  },
});

const lineLayer = (sourceID) => ({
  id: 'line-layer',
  type: 'line',
  source: sourceID,
  layout: {
    'line-join': 'round',
    'line-cap': 'round',
  },
  paint: {
    'line-color': '#888',
    'line-width': 4,
  },
});

export default {
  name: 'object-element-viewing-dialog',
  components: { MapComponent },
  mixins: [DialogBusMixin],
  props: {
    objectId: {
      type: String,
      required: true,
    },
    objectType: {
      type: String,
      default: 'layers',
    },
    mapType: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    sourceID: 'view-element-source',
    pointMarkers: [],
    geometryType: '',
    geometryId: null,
    settings: null,
    model: null,
  }),

  computed: {
    isPointType() {
      return ['Point', 'MultiPoint'].includes(this.geometryType);
    },

    isPolygonType() {
      return ['Polygon', 'MultiPolygon'].includes(this.geometryType);
    },

    isLineStringType() {
      return ['LineString', 'MultiLineString'].includes(this.geometryType);
    },
  },

  methods: {
    beforeOpen({ geometryId, settings, model = null }) {
      this.geometryId = geometryId;
      this.settings = settings;
      this.model = model;
    },

    onClose() {
      if (this.pointMarkers.length) {
        this.pointMarkers.forEach((point) => point.remove());
        this.pointMarkers = [];
      }

      if (this.layer) {
        const hasLayer = this.map.getLayer(this.layer.id);
        if (hasLayer) {
          this.map.removeLayer(this.layer.id);
          this.map.removeSource(this.sourceID);
        }
      }
    },

    async onMapCreated(map) {
      this.map = map;

      try {
        const data = await LayerGeometryFeatureRequest(
          this.objectType,
          this.objectId,
          this.geometryId,
        );

        const { location } = data.data;

        if (location) {
          this.geometryType = getGeometryType(location);

          if (this.isPointType) {
            if (location.geometries) {
              location.geometries.forEach(this.addPointOnMap);
              this.map.flyTo({ center: location.geometries[0].coordinates, zoom: 8 });
            } else {
              this.addPointOnMap(location);
              this.map.flyTo({ center: location.coordinates, zoom: 8 });
            }
          } else {
            this.addGeometryOnMap(location);
          }
        }
      } catch (e) {
        console.error(e);
      }
    },

    addBboxOnMap(geometry) {
      let bbox = null;

      if (geometry.geometries) bbox = turfBbox(geometry.geometries[0]);
      else bbox = turfBbox(geometry);

      if (bbox) this.map.fitBounds(bbox, { padding: 100 });
    },

    addPointOnMap(point) {
      const { coordinates } = point;
      const marker = new MapBox.Marker()
        .setDraggable(true)
        .setLngLat(coordinates)
        .addTo(this.map);
      this.pointMarkers.push(marker);
    },

    addGeometryOnMap(data) {
      this.addBboxOnMap(data);
      this.map.addSource(this.sourceID, {
        type: 'geojson', data,
      });

      if (this.isPolygonType) {
        this.layer = polygonLayer(this.sourceID, this.settings);
      } else if (this.isLineStringType) {
        this.layer = lineLayer(this.sourceID);
      }

      if (this.layer) this.map.addLayer(this.layer);
    },
  },
};
</script>

<style lang="scss" scoped>
.dialog-container {
  position: relative;
  height: 450px;
  width: 100%;
}
</style>
